import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { combineLatest, of, filter, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { KkmsDetailsService } from '../../../core/services/kkm-details.service';
import { TariffService } from '../../../core/services/tariff.service';
import { Tariff } from '../../tariffs/tariff.model';
import { ConfirmBySmsComponent } from '../../../shared/components/modals/confirm-by-sms/confirm-by-sms.component';
import { UsersService } from '../../users/user.service';
import { ModalService } from '../../../core/modal/modal.service';
import { CertificateModalSignatureData } from '../../../shared/services/signature-nca-common-utils.service';
import { IKkm } from '../../../core/modules/retail-places/retail-paces.types';
import { IOrganisation } from '../../../core/modules/organization/organization.types';
import { OrganizationApiService } from '../../../core/modules/organization/organization-api.service';
import { KkmsApiService } from '../../../core/modules/kkms/kkms-api.service';
import { BpService } from '../../../core/services/bp.service';
import { DeregisterKkmSignatureFields } from '../kkm-operations/deregister-kkm/deregister-kkm.model';
import { KkmsService } from '../../../core/services/kkms.service';

@UntilDestroy()
@Component({
  selector: 'tax-change-ofd-agreement',
  templateUrl: './change-ofd-agreement.component.html',
  styleUrls: ['./change-ofd-agreement.component.scss'],
})
export class ChangeOfdAgreementComponent implements OnInit {
  isAgreement = true;
  kkm?: IKkm;
  organization?: IOrganisation;
  tariff?: Tariff;
  data?: CertificateModalSignatureData;
  formFields?: DeregisterKkmSignatureFields;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private kkmsDetailsService: KkmsDetailsService,
    private organizationApiService: OrganizationApiService,
    private tariffService: TariffService,
    private usersService: UsersService,
    private modalService: ModalService,
    private kkmsApiService: KkmsApiService,
    private kkmsService: KkmsService,
    private bpService: BpService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.showDeclineReason();
    this.bpService.getCurrentBpFormData().pipe(
      switchMap((data) => {
        this.formFields = data.fields;
        return this.kkmsDetailsService.getKkm(data.fields.kkmId);
      }),
      switchMap((kkm) => {
        this.kkm = kkm;
        return combineLatest([
          this.kkmsApiService.fetchKkmRegistrationDocument(this.bpService.getProcessIds().pid),
          this.organizationApiService.fetchOrganization(),
          of(kkm),
        ]);
      }),
      switchMap(([document, org, kkm]) => {
        this.data = {
          doc: {
            signature: document,
          },
          type: 'SIGN',
        };
        this.organization = org;
        return kkm.tariffId ? this.tariffService.getTariffById(kkm.tariffId) : of(undefined);
      }),
      untilDestroyed(this),
    ).subscribe((tariff) => {
      this.tariff = tariff;
    });
  }

  signSms(): void {
    this.usersService.getCurrentUserInfo().pipe(
      switchMap(userInfo => this.modalService.openOld(ConfirmBySmsComponent, {
        title: 'sign.by.sms.title',
        buttonText: 'button.sign',
        businessProcessData: {
          IIN: userInfo.organisation.inn,
        },
        businessProcessMessage: 'inisSignBySmsMessage',
      })),
      filter(isSuccess => isSuccess),
      untilDestroyed(this),
    ).subscribe({
      next: () => this.router.navigate(['main/kkms']),
    });
  }

  signEds(signatureData: CertificateModalSignatureData): void {
    if (!signatureData.signature?.signature) return;
    this.formFields!.signature = signatureData.signature.signature;
    const { pid, tid } = this.bpService.getProcessIds();
    this.bpService.submit(pid, tid, this.formFields).pipe(untilDestroyed(this)).subscribe({
      next: () => this.router.navigate(['main/kkms']),
    });
  }

  back(): void {
    this.bpService.goToStepAndGetForm('inisBackToDeregistrationFormMessage', '', {})
      .pipe(untilDestroyed(this)).subscribe({
        next: () => this.location.back(),
      });
  }

  showDeclineReason(): void {
    const { declineReason } = this.route.snapshot.queryParams;
    if (declineReason) this.kkmsService.getDeclineReason(String(declineReason)).pipe(untilDestroyed(this)).subscribe();
  }
}
